import React, { useState, useMemo } from 'react';
import { formatInteger } from '../../utils/formatters';
import BetaChip from '../common/BetaChip/BetaChip';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import ExVesselValueModal from '../ExVesselValueModal/ExVesselValueModal';
import InsightColumnPair from '../InsightColumnPair';
import './JobsImpact.css';

function filterExvesselByYear(
  exvessel: unknown[] | undefined,
  selectedYear: string
): unknown[] {
  if (!exvessel || !Array.isArray(exvessel)) return [];
  const withYear = (item: unknown): number => (item as { year?: number })?.year ?? 0;
  if (selectedYear === 'historical') return exvessel.filter((item) => withYear(item) <= 2024);
  if (selectedYear === 'projections') return exvessel.filter((item) => withYear(item) >= 2025);
  return exvessel;
}

interface InsightData {
  totalJobs?: number;
  totalJobsUncertainty?: number;
  harvestingJobs?: number;
  processingJobs?: number;
  distributionJobs?: number;
  supportServicesJobs?: number;
  inducedJobs?: number;
}

interface JobsImpactData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  stockName?: string;
  onOpenExVesselModal?: () => void;
}

function getDescription(stockName?: string): string {
  const base =
    ', not just on fishing boats, but throughout the entire seafood supply chain and the broader economy.';
  if (!stockName || stockName === 'All Stocks') {
    return `Total number of jobs supported by all stocks${base}`;
  }
  return `Total number of jobs supported by ${stockName}${base}`;
}

export default function JobsImpact(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<JobsImpactData>(props);

  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;
  const pageTimeSeriesData = pageData?.data?.timeSeriesData as { exvessel?: unknown[] } | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;
  const stockName = injectionData.stockName ?? pageStockName;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const filteredExvessel = useMemo(
    () => filterExvesselByYear(pageTimeSeriesData?.exvessel, selectedYear),
    [pageTimeSeriesData?.exvessel, selectedYear]
  );
  const canOpenModal = selectedModel === 'WHAM';
  const onOpenExVesselModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;
  const description = getDescription(stockName);

  return (
    <div className="jobs-impact insight-card">
      <div className="insight-title-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 className="insight-title">Jobs Impact</h3>
          <BetaChip />
        </div>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle">{description}</h2>
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.totalJobs}
        currentValue={insights.totalJobs}
        statusQuoContent={
          <>
            {formatInteger(statusQuoDisplay.totalJobs)}
            <div className="uncertainty-value">
              (± {formatInteger(statusQuoDisplay.totalJobsUncertainty)})
            </div>
            <div className="secondary-value">
              Harvesting: {formatInteger(statusQuoDisplay.harvestingJobs)}
            </div>
            <div className="secondary-value">
              Processing: {formatInteger(statusQuoDisplay.processingJobs)}
            </div>
            <div className="secondary-value">
              Distribution: {formatInteger(statusQuoDisplay.distributionJobs)}
            </div>
            <div className="secondary-value">
              Support: {formatInteger(statusQuoDisplay.supportServicesJobs)}
            </div>
            <div className="secondary-value">
              Induced: {formatInteger(statusQuoDisplay.inducedJobs)}
            </div>
          </>
        }
        currentContent={
          <>
            {formatInteger(insights.totalJobs)}
            <div className="uncertainty-value">
              (± {formatInteger(insights.totalJobsUncertainty)})
            </div>
            <div className="secondary-value">
              Harvesting: {formatInteger(insights.harvestingJobs)}
            </div>
            <div className="secondary-value">
              Processing: {formatInteger(insights.processingJobs)}
            </div>
            <div className="secondary-value">
              Distribution: {formatInteger(insights.distributionJobs)}
            </div>
            <div className="secondary-value">
              Support: {formatInteger(insights.supportServicesJobs)}
            </div>
            <div className="secondary-value">
              Induced: {formatInteger(insights.inducedJobs)}
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenExVesselModal}
        clickable={Boolean(onOpenExVesselModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />

      <ExVesselValueModal
        isOpen={showModal}
        onClose={handleCloseModal}
        data={filteredExvessel}
        loading={pageLoading}
        error={pageError}
        stockName={stockName}
      />
    </div>
  );
}
